import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MyContext from "../ContextApi/MyContext";

const ProviderSwitcher = () => {
  const { activeProvider, approvedProviders, w9ApprovedProviders, switchProvider } = useContext(MyContext);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Unique approved Tax IDs (pending/declined Tax IDs aren't usable so they're omitted).
  // For each, remember whether at least one NPI under it has a W9 — used to label rows.
  const taxIdOptions = useMemo(() => {
    const w9Set = new Set(
      (w9ApprovedProviders || []).map((p) => (p.provider_no || "").trim())
    );
    const seen = new Set();
    const list = [];
    for (const p of approvedProviders || []) {
      const tid = (p.provider_no || "").trim();
      if (!tid || seen.has(tid)) continue;
      seen.add(tid);
      list.push({ taxId: tid, hasW9: w9Set.has(tid) });
    }
    return list;
  }, [approvedProviders, w9ApprovedProviders]);

  const activeTaxId = (activeProvider?.provider_no || "").trim();

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!activeTaxId) return null;

  // Single Tax ID: keep the original read-only badge
  if (taxIdOptions.length <= 1) {
    return (
      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
        Tax ID: {activeTaxId}
      </span>
    );
  }

  const handleSelect = (tid) => {
    if (tid === activeTaxId) {
      setOpen(false);
      return;
    }
    // Prefer a W9-approved NPI under this Tax ID; fall back to primary, then first.
    const allUnder = (approvedProviders || []).filter(
      (p) => (p.provider_no || "").trim() === tid
    );
    const w9One = allUnder.find(
      (p) => p.w9_status === "approved" || p.w9_status === "W9_form_uploaded"
    );
    const target = w9One || allUnder.find((p) => p.is_primary) || allUnder[0];
    if (target) switchProvider(target);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        onClick={() => setOpen((o) => !o)}
        className="text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded inline-flex items-center gap-1.5"
      >
        Tax ID: <span className="font-medium">{activeTaxId}</span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 min-w-[10rem] bg-white border border-gray-200 rounded shadow-lg z-50">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
            Switch Tax ID
          </div>
          {taxIdOptions.map(({ taxId, hasW9 }) => (
            <button
              key={taxId}
              onClick={() => handleSelect(taxId)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                taxId === activeTaxId
                  ? "bg-[#0486A5]/10 text-[#0486A5] font-medium"
                  : "text-gray-700"
              }`}
            >
              <span className="inline-flex items-center gap-2 flex-wrap">
                <span>{taxId}</span>
                {taxId === activeTaxId && (
                  <span className="text-[10px] text-[#0486A5]">(current)</span>
                )}
                {!hasW9 && (
                  <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5">
                    No W9
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderSwitcher;
